import { createInjectionState, type MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed, type ComputedRef, ref, unref } from 'vue';

import { tablerender } from '#public/commons-global';

export const ProgressType = {
    MAIN: 1,
    SECONDARY: 2,
} as const;
export type ProgressTypes = typeof ProgressType[keyof typeof ProgressType];

export type ProgressApi = {
    toggleModal(show?: boolean): Promise<void>;
    setProgress(percentage: number | undefined, progressMessage?: string, type?: ProgressTypes): void;
    setProgressLabel(progressLabel: string | undefined, type?: ProgressTypes): void;
    setTitle(title: string | undefined): void;
    setMessage(message: string | undefined, messageClass?: string): void;
    readonly isCancelled: ComputedRef<boolean>;
};

const [useProvideGlobalProgress, useGlobalProgressOptional] = createInjectionState((
    progress: ProgressApi,
) => ({
    progress,
}));

export function formatPercentage(percentage: number | undefined) {
    if((percentage === undefined) || (percentage < 0)) return '';
    let fixed = Number(percentage).toFixed(1);
    if(fixed.endsWith('.0')) {
        fixed = fixed.slice(0, -2);
    }
    return `${tablerender.renderPrettyNum(fixed)}%`;
}

export { useProvideGlobalProgress };

export function wrapProgressRef(
    api: MaybeRefOrGetter<ProgressApi | undefined>,
): ProgressApi {
    const progressApi: ProgressApi = {
        toggleModal(show) {
            const _api = toValue(api);
            return _api?.toggleModal(show) ?? Promise.resolve();
        },
        setProgress(percentage, progressMessage, type) {
            const _api = toValue(api);
            _api?.setProgress(percentage, progressMessage, type);
        },
        setProgressLabel(progressLabel, type) {
            const _api = toValue(api);
            _api?.setProgressLabel(progressLabel, type);
        },
        setTitle(title: string | undefined) {
            const _api = toValue(api);
            _api?.setTitle(title);
        },
        setMessage(message: string | undefined, messageClass?: string) {
            const _api = toValue(api);
            _api?.setMessage(message, messageClass);
        },
        isCancelled: computed(() => {
            const _api = toValue(api);
            return unref(_api?.isCancelled) ?? false;
        }),
    };
    return progressApi;
}

export function useProgress(provide?: boolean) {
    const progressRef = ref<ProgressApi | undefined>();
    const progressApi = wrapProgressRef(progressRef);

    if(provide) {
        useProvideGlobalProgress(progressApi);
    }

    return [progressRef, progressApi] as const;
}

export function useGlobalProgress() {
    const handler = useGlobalProgressOptional();
    if(!handler) {
        throw new Error('Progress is not provided');
    }
    return handler.progress;
}
